import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { ProductEnum } from '../authentication/models/product.enum';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { AdvanceOnlyStateService } from './advance-only-state.service';
import { canMoveMoneyWithoutAccount } from './tools/accountless-access.tools';

export const moveMoneyAccessGuard: CanActivateFn = () => {
  const profile = inject(AuthenticationService).getProfileState();
  return (
    (!!profile &&
      (profile.featureFlagIncrease ||
        profile.productIds?.includes(ProductEnum.Payment) ||
        (profile.productIds?.includes(ProductEnum.Advance) &&
          !profile.disableAdvances &&
          !profile.disableWithdrawals))) ||
    inject(Router).createUrlTree(['/wallet'])
  );
};

export const walletFeatureGuard: CanActivateFn = async (route) => {
  const state = inject(AdvanceOnlyStateService);
  const router = inject(Router);
  const auth = inject(AuthenticationService);
  if (route.routeConfig?.path === 'move-money') {
    const user = auth.getProfileState();
    if (canMoveMoneyWithoutAccount(user)) return true;
    // Restrictions on advances do not block transfers of existing account funds.
    if (user?.featureFlagIncrease) {
      try {
        if (!(await state.refresh())) return true;
      } catch {
        /* Unknown account state cannot authorize this fallback. */
      }
    }
    return router.createUrlTree(['/wallet']);
  }
  try {
    if ((await state.refresh()) && !auth.getProfileState()?.featureFlagIncrease)
      return router.createUrlTree([
        '/account-preview',
        route.routeConfig?.path,
      ]);
  } catch {
    return router.createUrlTree(['/wallet']);
  }
  return (
    auth.getProfileState()?.featureFlagIncrease === true ||
    router.createUrlTree(['/wallet'])
  );
};

/** Home is separate; the established advance-only screen remains the Advance route. */
export const advanceHomeGuard: CanActivateFn = async (route) => {
  const auth = inject(AuthenticationService);
  const state = inject(AdvanceOnlyStateService);
  const router = inject(Router);
  try {
    const withoutAccount = await state.refresh();
    if (
      route.routeConfig?.path === 'advance' &&
      withoutAccount &&
      !auth.getProfileState()?.productIds?.includes(ProductEnum.Payment)
    )
      return router.createUrlTree(['/advance-only'], {
        queryParams: route.queryParams,
        fragment: route.fragment ?? undefined,
      });
  } catch {
    /* Home displays its own retryable balance error. */
  }
  return true;
};

export const advancePreviewGuard: CanActivateFn = async (route) => {
  if (
    route.routeConfig?.path === 'advance-only' &&
    inject(AuthenticationService)
      .getProfileState()
      ?.productIds?.includes(ProductEnum.Payment)
  )
    return inject(Router).createUrlTree(['/advance'], {
      queryParams: route.queryParams,
      fragment: route.fragment ?? undefined,
    });
  const feature = route.paramMap?.get('feature');
  if (
    inject(AuthenticationService).getProfileState()?.featureFlagIncrease &&
    ['pockets', 'cards', 'coming-soon', 'benefits'].includes(feature || '')
  )
    return inject(Router).createUrlTree([
      feature === 'cards' ? '/cards' : '/pockets',
    ]);
  if (route.paramMap?.get('feature') === 'move-money')
    return inject(Router).createUrlTree(['/move-money']);
  const state = inject(AdvanceOnlyStateService);
  const router = inject(Router);
  try {
    if (await state.refresh()) return true;
  } catch {
    return router.createUrlTree(['/wallet']);
  }
  return router.createUrlTree([
    feature && ['cards', 'pockets', 'move-money'].includes(feature)
      ? '/' + feature
      : '/advance',
  ]);
};

export const accountlessActivityGuard: CanActivateFn = async () => {
  const user = inject(AuthenticationService).getProfileState();
  if (
    user?.featureFlagIncrease ||
    user?.productIds?.includes(ProductEnum.Payment)
  )
    return true;
  const state = inject(AdvanceOnlyStateService);
  const router = inject(Router);
  try {
    return (
      (await state.refresh()) || router.createUrlTree(['/wallet-activity'])
    );
  } catch {
    return router.createUrlTree(['/wallet']);
  }
};
